import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, BadgeDollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const today = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

const PaymentForm = ({ init, methods, onSave, onCancel, saving }) => {
  const [f, setF] = useState(init);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid grid-cols-12 gap-2 items-center bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid="so-payment-form">
      <Input type="date" value={f.payment_date} onChange={set('payment_date')} className="col-span-3 h-8 rounded-sm" data-testid="so-payment-date" />
      <Input type="number" min="0.01" step="0.01" value={f.amount} onChange={set('amount')} placeholder="Amount" className="col-span-2 h-8 rounded-sm mono text-right" data-testid="so-payment-amount" />
      <select value={f.method} onChange={set('method')} className="col-span-2 h-8 border border-slate-200 rounded-sm px-2 text-sm bg-white" data-testid="so-payment-method">
        {methods.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <Input value={f.reference || ''} onChange={set('reference')} placeholder="Reference / cheque #" className="col-span-2 h-8 rounded-sm" data-testid="so-payment-reference" />
      <Input value={f.notes || ''} onChange={set('notes')} placeholder="Notes" className="col-span-2 h-8 rounded-sm" data-testid="so-payment-notes" />
      <div className="col-span-1 flex gap-1 justify-end">
        <Button size="icon" className="h-8 w-8 bg-emerald-700 hover:bg-emerald-800" disabled={saving} onClick={() => onSave(f)} data-testid="so-payment-save"><Check className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onCancel} data-testid="so-payment-cancel"><X className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const SoPaymentsCard = ({ api, soId, soStatus, onTotals }) => {
  const [data, setData] = useState(null);
  const [methods, setMethods] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([api().get(`/sales-orders/${soId}/payments`), api().get('/payment-methods')]);
      setData(p.data); setMethods(m.data.methods || []);
    } catch { toast.error('Failed to load payments'); }
  }, [api, soId]);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    if (!(Number(f.amount) > 0)) { toast.error('Enter an amount above $0'); return; }
    setSaving(true);
    try {
      const body = { ...f, amount: Number(f.amount) };
      const r = editing === 'new' ? await api().post(`/sales-orders/${soId}/payments`, body) : await api().put(`/sales-orders/${soId}/payments/${editing}`, body);
      toast.success(editing === 'new' ? `Payment of ${money(body.amount)} recorded` : 'Payment updated');
      setEditing(null); onTotals(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${money(p.amount)} ${p.method} payment?`)) return;
    try { const r = await api().delete(`/sales-orders/${soId}/payments/${p.id}`); onTotals(r.data); load(); toast.success('Payment deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (!data) return null;
  const canAdd = soStatus !== 'Canceled';
  return (
    <Card className="industrial-card mb-20" data-testid="so-payments-card">
      <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><BadgeDollarSign className="w-4 h-4" /> Payments</CardTitle>
        {canAdd && editing === null && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditing('new')} data-testid="so-add-payment-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Record Payment</Button>}
      </CardHeader>
      <CardContent className="p-4 space-y-2">
        {data.deposit_amount > 0 && (
          <div className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5" data-testid="so-payment-deposit-row">
            <span className="text-slate-600">Deposit (on order)</span><span className="mono">{money(data.deposit_amount)}</span>
          </div>
        )}
        {data.payments.map(p => (editing === p.id ? (
          <PaymentForm key={p.id} init={p} methods={methods.includes(p.method) ? methods : [...methods, p.method]} onSave={save} onCancel={() => setEditing(null)} saving={saving} />
        ) : (
          <div key={p.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5" data-testid={`so-payment-row-${p.id}`}>
            <span><span className="mono">{p.payment_date}</span> · <strong>{p.method}</strong>{p.reference && <span className="text-slate-500"> · {p.reference}</span>}{p.notes && <span className="text-slate-400"> · {p.notes}</span>}<span className="text-[11px] text-slate-400"> · by {p.created_by_name}</span></span>
            <span className="flex items-center gap-1">
              <span className="mono font-semibold">{money(p.amount)}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(p.id)} data-testid={`so-payment-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(p)} data-testid={`so-payment-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </span>
          </div>
        )))}
        {editing === 'new' && <PaymentForm init={{ payment_date: today(), amount: data.balance_due > 0 ? data.balance_due.toFixed(2) : '', method: methods[0] || '', reference: '', notes: '' }} methods={methods} onSave={save} onCancel={() => setEditing(null)} saving={saving} />}
        {!data.payments.length && editing !== 'new' && <p className="text-xs text-slate-400">No payments recorded yet.</p>}
        <div className="flex justify-end gap-6 text-sm pt-1" data-testid="so-payments-summary">
          <span>Paid: <strong className="mono" data-testid="so-amount-paid">{money(data.deposit_amount + data.payments_total)}</strong></span>
          <span>Balance Due: <strong className={`mono ${data.balance_due <= 0 ? 'text-emerald-700' : ''}`} data-testid="so-payments-balance">{money(data.balance_due)}</strong></span>
        </div>
      </CardContent>
    </Card>
  );
};

export default SoPaymentsCard;
